import { Card } from '@populix/pop-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { IoMdWarning } from 'react-icons/io'

import { Loading, NotificationSettingsItem } from '..'

import postNotificationSettings from '@/libs/actions/notifications/post-notification-settings'
import { NOTIFICATION_SETTINGS_QUERY_KEY } from '@/libs/apis/queries/use-get-notification-settings'
import getErrorMessage from '@/libs/helpers/get-error-message'
import { useAppDispatch } from '@/libs/hooks/use-app-dispatch'
import { useAppSelector } from '@/libs/hooks/use-app-selector'
import useFcmToken from '@/libs/hooks/use-fcm-token'
import useGetUser from '@/libs/hooks/use-get-user'
import useToast from '@/libs/hooks/use-toast'
import { setShowNotificationRequest } from '@/libs/store/slice/ui-slice'

type TNotificationSettingsCard = {
  readonly defaultStatus: { webPushNotif: boolean; email: boolean }
}
export default function NotificationSettingsCard({ defaultStatus }: TNotificationSettingsCard) {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState(defaultStatus)
  const [loading, setLoading] = useState(false)
  const { partnerId, partnerIdNonIntegration } = useAppSelector((state) => state.user)
  const { push } = useRouter()
  const { queryInfo } = useGetUser()

  const { show: showToast, close: closeToast } = useToast()
  const {
    requestNotificationPermission,
    notificationPermissionStatus,
    loading: isNotifLoading,
  } = useFcmToken()

  const hasPartnerId = partnerId || partnerIdNonIntegration

  useEffect(() => {
    if (!('Notification' in window) && queryInfo?.isGuest) push('/')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      !hasPartnerId &&
      status.webPushNotif &&
      notificationPermissionStatus !== 'denied' &&
      'Notification' in window
    )
      requestNotificationPermission().catch((error) => {
        showErrorMessage(error)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClick = ({
    state,
    settings,
  }: {
    state: boolean
    settings: 'PUSH_NOTIF' | 'EMAIL'
  }) => {
    if (settings === 'PUSH_NOTIF') {
      setStatus((prev) => ({ ...prev, webPushNotif: state }))
      dispatch(setShowNotificationRequest(false))

      if (state) return handleTurnOnWebPush()
      return handleUpdatesettings({ type: 'PUSH_NOTIF', status: state })
    }

    setStatus((prev) => ({ ...prev, email: state }))
    return handleUpdatesettings({ type: 'EMAIL', status: state })
  }

  const handleTurnOnWebPush = async () => {
    await postNotificationSettings({ value: true, type: 'PUSH_NOTIF' })

    await requestNotificationPermission()
      .then(() => {
        if (notificationPermissionStatus === 'granted') {
          showSuccessMessage({ status: true, type: 'PUSH_NOTIF' })
        }
      })
      .catch((error) => {
        showErrorMessage(error)
      })
  }

  const handleUpdatesettings = async ({
    status,
    type,
  }: {
    status: boolean
    type: 'EMAIL' | 'PUSH_NOTIF'
  }) => {
    setLoading(true)
    const payload = {
      type,
      value: status,
    }
    const res = await postNotificationSettings(payload)
    if (!res?.success) {
      showToast({
        title: res?.error?.code ?? 'Error Occured',
        description: res?.error?.message,
        type: 'error',
        traceId: res?.traceId,
      })
      return setLoading(false)
    }

    queryClient.invalidateQueries({ queryKey: [NOTIFICATION_SETTINGS_QUERY_KEY] })
    showSuccessMessage({ status, type })
    return setLoading(false)
  }

  const showErrorMessage = (error: unknown) =>
    showToast({
      type: 'warning',
      title: getErrorMessage(error),
      onClose: closeToast,
    })

  const showSuccessMessage = ({
    status,
    type,
  }: {
    status: boolean
    type: 'EMAIL' | 'PUSH_NOTIF'
  }) =>
    showToast({
      type: 'success',
      title: `${type === 'EMAIL' ? 'Email Notification' : 'Push Notification'} ${
        status ? 'Subscribed' : 'Unsubscribed'
      }`,
      onClose: closeToast,
    })

  return (
    <Card noElevation>
      <p className='font-semibold'>Notification</p>
      <hr />
      {!hasPartnerId && 'Notification' in window && (
        <NotificationSettingsItem
          title='Push Notification'
          state={status.webPushNotif}
          onClick={(state: boolean) => handleClick({ state, settings: 'PUSH_NOTIF' })}
        />
      )}
      {!hasPartnerId && notificationPermissionStatus === 'denied' && status.webPushNotif && (
        <div className='flex flex-row items-center gap-1'>
          <IoMdWarning className='text-pop-alert' />
          <p className='text-xs text-pop-grey-800'>Your browser block notification</p>
        </div>
      )}
      <NotificationSettingsItem
        title='Email Notification'
        state={status.email}
        onClick={(state: boolean) => handleClick({ state, settings: 'EMAIL' })}
      />
      <Loading appear={loading || isNotifLoading} />
    </Card>
  )
}
